import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';

function Dropdown({
  variant = 'selection',
  trigger,
  triggerIcon = 'ChevronDown',
  label,
  items = [],
  value,
  onChange,
  multiSelect = false,
  placeholder = 'Select an option',
  disabled = false,
  className = '',
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState(multiSelect ? (value || []) : (value ? [value] : []));
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (multiSelect) {
      setSelectedItems(value || []);
    } else {
      setSelectedItems(value ? [value] : []);
    }
  }, [value, multiSelect]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Enter' || e.key === ' ') {
      toggleDropdown();
    }
  };

  const handleItemClick = (item) => {
    if (multiSelect) {
      const isSelected = selectedItems.some(selectedItem =>
        selectedItem.value === item.value
      );

      const newSelectedItems = isSelected
        ? selectedItems.filter(selectedItem => selectedItem.value !== item.value)
        : [...selectedItems, item];

      setSelectedItems(newSelectedItems);

      if (onChange) {
        onChange(newSelectedItems);
      }
    } else {
      setSelectedItems([item]);
      setIsOpen(false);

      if (onChange) {
        onChange(item);
      }
    }
  };

  // Navigation Dropdown
  if (variant === 'navigation') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef} {...props}>
        <button
          type="button"
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          className={`flex items-center justify-between w-full rounded-md border border-slate-200 bg-white px-3 text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl shadow-sm
              h-[52px] 
              focus:outline-none focus:ring-2 focus:ring-primary-600
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-slate-300'}
              ${className}`}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          disabled={disabled}
        >

          {trigger || 'Menu'}
          <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={16} />
        </button>

        {isOpen && (
          <div className="absolute z-10 mt-1 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none animate-fade-in">
            <div className="py-1" role="menu" aria-orientation="vertical">
              {items.map((item, index) => (
                <a
                  key={index}
                  href={item.href || '#'}
                  className="block px-4 py-2 text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                  role="menuitem"
                  onClick={(e) => {
                    if (!item.href) {
                      e.preventDefault();
                      setIsOpen(false);
                      if (item.onClick) item.onClick();
                    }
                  }}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Selection Dropdown
  if (variant === 'selection') {
    const displayValue = selectedItems.length > 0
      ? (multiSelect
        ? `${selectedItems.length} selected`
        : selectedItems[0].label)
      : placeholder;

    return (
      <div className={`relative ${className}`} ref={dropdownRef} {...props}>
        {label && (
          <label className="block text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl font-medium text-slate-700 mb-1">
            {label}
          </label>
        )}

        <button
          type="button"
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          className={`flex items-center justify-between w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-600 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-slate-300'}`}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          disabled={disabled}
        >
          <span className={`block truncate ${selectedItems.length === 0 ? 'text-slate-400' : 'text-slate-900'}`}>
            {displayValue}
          </span>
          <Icon name={isOpen ? 'ChevronUp' : triggerIcon} size={16} className="text-slate-400" />
        </button>

        {isOpen && (
          <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none animate-fade-in">
            <ul className="py-1" role="listbox" aria-multiselectable={multiSelect}>
              {items.map((item, index) => {
                const isSelected = selectedItems.some(selectedItem =>
                  selectedItem.value === item.value
                );

                return (
                  <li
                    key={index}
                    className={`cursor-pointer select-none relative py-2 pl-3 pr-9 text-slate-900 ${isSelected ? 'bg-primary-50' : 'hover:bg-slate-100'}`}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleItemClick(item)}
                  >
                    <div className="flex items-center">
                      {multiSelect && (
                        <span className="mr-3 flex h-5 w-5 items-center justify-center rounded border border-slate-300 bg-white">
                          {isSelected && <Icon name="Check" size={12} className="text-primary-600" />}
                        </span>
                      )}
                      <span className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}>
                        {item.label}
                      </span>
                    </div>

                    {!multiSelect && isSelected && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-600">
                        <Icon name="Check" size={16} />
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Multi-select Dropdown
  if (variant === 'multi-select') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef} {...props}>
        {label && (
          <label className="block text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl font-medium text-slate-700 mb-1">
            {label}
          </label>
        )}

        <div
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          className={`flex flex-wrap items-center gap-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-600 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-slate-300'}`}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          {selectedItems.length > 0 ? (
            selectedItems.map((item, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 bg-primary-50 text-primary-700 rounded-full px-2 py-0.5 text-xs font-medium"
              >
                {item.label}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleItemClick(item);
                  }}
                  className="text-primary-500 hover:text-primary-700 focus:outline-none"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            ))
          ) : (
            <span className="text-slate-400">{placeholder}</span>
          )}
          <span className="ml-auto">
            <Icon name={isOpen ? 'ChevronUp' : triggerIcon} size={16} className="text-slate-400" />
          </span>
        </div>

        {isOpen && (
          <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none animate-fade-in">
            <ul className="py-1" role="listbox" aria-multiselectable={true}>
              {items.map((item, index) => {
                const isSelected = selectedItems.some(selectedItem =>
                  selectedItem.value === item.value
                );

                return (
                  <li
                    key={index}
                    className={`cursor-pointer select-none relative py-2 pl-3 pr-9 text-slate-900 ${isSelected ? 'bg-primary-50' : 'hover:bg-slate-100'}`}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleItemClick(item)}
                  >
                    <div className="flex items-center">
                      <span className="mr-3 flex h-5 w-5 items-center justify-center rounded border border-slate-300 bg-white">
                        {isSelected && <Icon name="Check" size={12} className="text-primary-600" />}
                      </span>
                      <span className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}>
                        {item.label}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Filter Dropdown
  if (variant === 'filter') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef} {...props}>
        <button
          type="button"
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          className={`inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-600 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-slate-300'}`}
          aria-expanded={isOpen}
          aria-haspopup="true"
          disabled={disabled}
        >
          <Icon name="Filter" size={16} className="text-slate-500" />
          {trigger || 'Filter'}
          <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-slate-400" />
        </button>

        {isOpen && (
          <div className="absolute right-0 z-10 mt-1 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none animate-fade-in">
            <div className="p-2">
              {items.map((item, index) => (
                <div key={index} className="mb-1 last:mb-0">
                  <button
                    type="button"
                    className={`flex w-full items-center rounded-md px-2 py-1.5 text-sm ${selectedItems.some(selectedItem => selectedItem.value === item.value)
                      ? 'bg-primary-50 text-primary-700' : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    onClick={() => handleItemClick(item)}
                  >
                    <span className="mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-slate-300">
                      {selectedItems.some(selectedItem => selectedItem.value === item.value) && (
                        <Icon name="Check" size={10} className="text-primary-600" />
                      )}
                    </span>
                    {item.label}
                  </button>
                </div>
              ))}
            </div>

            {multiSelect && (
              <div className="border-t border-slate-200 p-2 flex justify-end gap-2">
                <button
                  type="button"
                  className="text-xs px-2 py-1 text-slate-700 hover:bg-slate-100 rounded"
                  onClick={() => {
                    setSelectedItems([]);
                    if (onChange) onChange([]);
                  }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="text-xs px-2 py-1 bg-primary-600 text-white rounded hover:bg-primary-700"
                  onClick={() => setIsOpen(false)}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Default dropdown
  return (
    <div className={`relative ${className}`} ref={dropdownRef} {...props}>
      <button
        type="button"
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        className="inline-flex items-center gap-1 text-slate-700 hover:text-primary-600 focus:outline-none"
        aria-expanded={isOpen}
        aria-haspopup="true"
        disabled={disabled}
      >
        {trigger || 'Options'}
        <Icon name={triggerIcon} size={16} />
      </button>

      {isOpen && (
        <div className="absolute right-0 z-10 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none animate-fade-in">
          <div className="py-1" role="menu" aria-orientation="vertical">
            {items.map((item, index) => (
              <button
                key={index}
                type="button"
                className="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                role="menuitem"
                onClick={() => {
                  setIsOpen(false);
                  if (item.onClick) item.onClick();
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dropdown;